import React, { useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

interface UploadModalProps {
  showUploadModal: boolean;
  setShowUploadModal: (show: boolean) => void;
  fetchTrackImages: () => Promise<void>;
}

const UploadModal: React.FC<UploadModalProps> = ({
  showUploadModal,
  setShowUploadModal,
  fetchTrackImages
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    track_type: 'drag',
    is_featured: false,
    is_event_flyer: false,
    file: null as File | null
  });

  const handleTrackImageUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please select a file to upload');
      return;
    }

    setLoading(true);
    try {
      const fileExt = uploadForm.file.name.split('.').pop();
      const filePath = `${uploadForm.track_type}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('track-images')
        .upload(filePath, uploadForm.file);

      if (uploadError) throw uploadError;

      const { data: imageData, error: dbError } = await supabase
        .from('track_images')
        .insert([
          {
            title: uploadForm.title,
            description: uploadForm.description,
            track_type: uploadForm.track_type,
            storage_path: filePath,
            is_featured: uploadForm.is_featured,
            is_event_flyer: uploadForm.is_event_flyer
          }
        ])
        .select()
        .single();

      if (dbError) throw dbError;

      toast.success('Event flyer uploaded successfully');
      setShowUploadModal(false);
      setUploadForm({
        title: '',
        description: '',
        track_type: 'drag',
        is_featured: false,
        is_event_flyer: false,
        file: null
      });
      fetchTrackImages();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  if (!showUploadModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">Create Event</h2>
          <button onClick={() => setShowUploadModal(false)}>
            <X className="h-6 w-6 text-gray-400 hover:text-white" />
          </button>
        </div>
        <form onSubmit={handleTrackImageUpload} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              required
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              required
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white h-24"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Track Type
            </label>
            <select
              value={uploadForm.track_type}
              onChange={(e) => setUploadForm({ ...uploadForm, track_type: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
            >
              <option value="drag">Drag</option>
              <option value="drift">Drift</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Image File
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
              required
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={uploadForm.is_featured}
                onChange={(e) => setUploadForm({ ...uploadForm, is_featured: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm text-gray-300">Featured Image</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={uploadForm.is_event_flyer}
                onChange={(e) => setUploadForm({ ...uploadForm, is_event_flyer: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm text-gray-300">Event Flyer</span>
            </label>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Event'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UploadModal;